import { cva, type VariantProps } from 'class-variance-authority'
import React, { ReactNode } from 'react'
import {
  HiCheckCircle,
  HiExclamationCircle,
  HiExclamationTriangle,
  HiInformationCircle,
  HiXMark,
} from 'react-icons/hi2'
import { cn } from './cn'
import { IconButton } from './IconButton'

const bannerVariants = cva(
  'flex items-start gap-3 rounded-md border border-solid px-4 py-3 font-text text-[14px] leading-[1.5]',
  {
    variants: {
      tone: {
        info: 'border-info/30 bg-info-bg text-fg [--banner-icon:var(--plug-info)]',
        success: 'border-success/30 bg-success-bg text-fg [--banner-icon:var(--plug-success)]',
        warning: 'border-warning/30 bg-warning-bg text-fg [--banner-icon:var(--plug-warning)]',
        danger: 'border-danger/30 bg-danger-bg text-fg [--banner-icon:var(--plug-danger)]',
      },
    },
    defaultVariants: { tone: 'info' },
  }
)

const ICONS = {
  info: HiInformationCircle,
  success: HiCheckCircle,
  warning: HiExclamationTriangle,
  danger: HiExclamationCircle,
}

export interface BannerProps extends VariantProps<typeof bannerVariants> {
  title?: ReactNode
  children?: ReactNode
  icon?: ReactNode | false
  action?: ReactNode
  /** Shows a dismiss button. */
  onClose?: () => void
  closeLabel?: string
  className?: string
}

/** Inline status message (replaces MUI Alert). */
export const Banner = ({
  tone = 'info',
  title,
  children,
  icon,
  action,
  onClose,
  closeLabel = 'Dismiss',
  className,
}: BannerProps) => {
  const Icon = ICONS[tone ?? 'info']
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn(bannerVariants({ tone }), className)}
    >
      {icon !== false && (
        <span className="mt-0.5 flex shrink-0 text-[18px] text-[var(--banner-icon)]">
          {icon ?? <Icon aria-hidden="true" />}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {title && <span className="font-semibold">{title}</span>}
        {children && <div className="text-fg-2 [&_a]:text-link">{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
      {onClose && (
        <IconButton
          aria-label={closeLabel}
          size="sm"
          onClick={onClose}
          className="-my-1 -mr-2 text-fg-2"
        >
          <HiXMark />
        </IconButton>
      )}
    </div>
  )
}
